<template>
  <div class="player-wrap">
    <div class="player-mask" :style="{ background: maskBg }" draggable="false"></div>
    <div class="player-progress-bar">
      <CacheBar :visible="hasCache" @jumpto="jumpTo" />
      <div
        class="playing-bar"
        :class="{ 'enable-drag': dragging }"
        @click.self.stop.prevent="jumpTo"
        :style="{ width: `${playConfig.played}%` }"
      >
        <div class="slider-btn" draggable="false" @mousedown.self.stop.prevent="enableDrag"></div>
      </div>
    </div>
    <div class="player-control-wrap">
      <div class="start" @click="toggleState">
        <span class="iconfont icon-start" v-if="playConfig.status"></span>
        <span class="iconfont icon-stop" v-else></span>
      </div>
      <div class="time-panel">{{ timeText }}</div>
    </div>
  </div>
</template>

<script>
import CacheBar from './cache-bar.vue';

import { isArray } from '@/utils/is';
import { formatTime, toTimestamp } from '@/utils/date';
import { timer } from '@/utils/utils';

function debounce(fn, delay) {
  var timeout = null;
  let flag = false;
  return function () {
    if (flag) return;
    flag = true;
    fn(...arguments);
    timeout = setTimeout(() => {
      flag = false;
      clearTimeout(timeout);
    }, delay);
  };
}

export default {
  components: { CacheBar },
  props: {
    range: {
      type: Array,
      default: () => []
    },
    cacheRanges: {
      type: Array,
      default: () => []
    },
    defaultActive: {
      type: Number,
      default: 0
    },
    speed: {
      type: Number,
      default: 1
    },
    frame: {
      type: Number,
      default: 30
    },
    maskBg: {
      type: String,
      default: 'linear-gradient(to bottom, rgba(19, 19, 19, 0.3), rgba(79, 79, 79, 1))'
    }
  },
  data() {
    const activeTime = this.defaultActive || this.range[0] || 0;
    return {
      width: '',
      interval: null,
      dragging: false,
      activeTime,
      playConfig: {
        played: 0,
        status: 1,
        break: 0
      },
      mousemoveCallback: null,
      eventRecord: []
    };
  },
  computed: {
    hasCache() {
      return isArray(this.cacheRanges) && !!this.cacheRanges.length;
    },
    max() {
      return +(isArray(this.range) && this.range[1]) || 0;
    },
    min() {
      return +(isArray(this.range) && this.range[0]) || 0;
    },
    dvalue() {
      return this.max - this.min;
    },
    increment() {
      return (100 / (this.dvalue / 1000) / this.frame) * this.speed;
    },
    timeText() {
      const fmt = 'hh:mm:ss';
      if (isArray(this.range) && !this.range.length) return `00:00:00/00:00:00`;
      return `${formatTime(this.activeTime, fmt)}/${formatTime(this.max, fmt)}`;
    }
  },
  provide() {
    return {
      root: this
    };
  },
  methods: {
    inCache(time, type) {
      if (!this.hasCache) return false;
      if (type === 'percent') {
        time = Math.min(100, time);
        time = (time / 100) * this.dvalue + this.min;
      }
      const res = this.cacheRanges.some(range => time >= range[0] && time <= range[1]);
      return res;
    },
    initElInfo() {
      const callback = () => {
        const el = this.$el.querySelector('.player-progress-bar');
        this.width = el.clientWidth;
      };
      this.eventRecord.push([window, 'resize', callback]);
      callback();
      window.addEventListener('resize', callback);
    },
    /** 清除事件监听 */
    clearEvent() {
      this.eventRecord.forEach(([root, eventName, callback]) => {
        root.removeEventListener(eventName, callback);
      });
    },
    restart() {
      this.breakPlay(1);
      this.setPlayed(0);
      timer(() => this.breakPlay(0), 200);
    },
    setPlayState(state) {
      this.playConfig.status = state;

      this.$emit('playState', state);
    },
    /** 切换按钮，并触发事件 */
    toggleState() {
      this.playConfig.status = +!this.playConfig.status;

      this.$emit('playState', this.playConfig.status);
    },
    getTimeByPercent(percent) {
      const timestamp = this.min + (this.dvalue * percent) / 100;
      return toTimestamp(timestamp);
    },
    /**
     * 设置播放进度
     * @param {number} width 0~100
     * @param {Element} el
     */
    setPlayed(percent, event) {
      percent = Math.max(0, percent);
      percent = Math.min(100, percent);
      this.activeTime = this.getTimeByPercent(percent);
      this.playConfig.played = percent;

      this.$nextTick(() => {
        this.$emit('change', this.activeTime);
        if (event) this.$emit(event, this.activeTime);
      });
    },
    /**
     * 中断自动播放
     * @param {0|1} flag 0启动，1中断
     */
    breakPlay(flag) {
      this.playConfig.break = +flag;
    },
    /** 进度条跳到指定位置 */
    jumpTo(event) {
      this.breakPlay(1);
      const offsetX = event.offsetX;
      this.setPlayed((offsetX / this.width) * 100, 'jumpto');
      timer(() => this.breakPlay(0), 500);
    },
    /**
     * 启用进度条拖拽
     * @param {Event} event
     */
    enableDrag(event) {
      event.stopPropagation();
      event.preventDefault();

      this.dragging = true;

      this.breakPlay(1);
      let begin = event.clientX;
      let end = 0;
      let dvalue = 0;
      let width = 0;
      let first = true;
      const callback = debounce((/** @type {Event} */ e) => {
        e.stopPropagation();
        e.preventDefault();
        end = e.clientX;
        dvalue = end - begin;
        width = (dvalue / this.width) * 100;
        width += +this.playConfig.played;
        if (!this.inCache(width, 'percent')) return;
        first = false;
        begin = end;
        this.setPlayed(width);
      }, 50);

      const remove = () => {
        if (!first) this.setPlayed(width, 'jumpto');
        timer(() => this.breakPlay(0), 200);

        this.dragging = false;

        window.removeEventListener('mouseup', remove);
        window.removeEventListener('mousemove', callback);
      };
      window.addEventListener('mousemove', callback);
      window.addEventListener('mouseup', remove);
    },
    /**
     * 自动播放
     * @param {0|1} state 启动状态0:off|1:on
     */
    autoPlay(state) {
      if (this.interval) clearInterval(this.interval);
      if (!state) return;

      const frame = this.frame;
      this.interval = setInterval(() => {
        if (this.playConfig.break) return;
        if (this.playConfig.played === 100) return this.setPlayState(0);

        const { increment } = this;

        if (!this.inCache(this.playConfig.played + increment, 'percent')) return;
        this.setPlayed((this.playConfig.played += increment));
      }, 1000 / frame);
    }
  },
  watch: {
    'playConfig.status': {
      handler: 'autoPlay',
      immediate: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initElInfo();
    });
  },
  beforeDestroy() {
    this.clearEvent();
  }
};
</script>

<style lang="scss">
.player-wrap {
  width: 100%;
  height: 60px;
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 10;
  padding-top: 10px;
  user-select: none;

  .player-progress-bar {
    width: calc(100% - 30px);
    height: 10px;
    position: relative;
    left: 15px;
    z-index: 2;

    background: rgba(76, 96, 98, 0.6);
    border-radius: 8px;
    box-sizing: border-box;

    .playing-bar {
      height: 10px;
      position: absolute;
      left: 0;
      z-index: 2;

      background: #eee;
      cursor: pointer;
      border-radius: 8px;

      &.enable-drag {
        transition: width 0.1s;
      }

      .slider-btn {
        width: 15px;
        height: 15px;
        position: absolute;
        right: 0;
        top: 50%;
        transform: translate(50%, -50%);

        border-radius: 50%;
        background: #49c7c3;
        border: 1px solid transparent;
        cursor: pointer;

        // &:hover {
        //   border: 1px solid yellow;
        // }
      }
    }
  }

  .player-control-wrap {
    color: white;
    display: flex;
    position: relative;
    z-index: 2;
    padding-top: 3px;
    padding-left: 7px;
    .start {
      padding: 8px;
      cursor: pointer;
      .iconfont {
        font-size: 24px;
      }
    }
    .time-panel {
      display: flex;
      align-items: center;
    }
  }

  .player-mask {
    width: 100%;
    height: 100%;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1;
  }
}
</style>
